import type { CaptionItem, EditCommand } from '@repo/editor-core';
import { CAPTION_FONTS } from './fonts';

const FONT_GROUPS: { label: string; group: 'ko' | 'latin' | 'system' }[] = [
  { label: '한글', group: 'ko' },
  { label: '영문 (라틴)', group: 'latin' },
  { label: '시스템', group: 'system' },
];
const ALIGNMENTS: { value: CaptionItem['style']['alignment']; label: string }[] = [
  { value: 'top', label: '상단' },
  { value: 'center', label: '중앙' },
  { value: 'bottom', label: '하단' },
];

/** Edit a selected caption's text and style. Each control dispatches editCaption. */
export function CaptionInspector({
  caption,
  dispatch,
  onError,
}: {
  caption: CaptionItem;
  dispatch: (cmd: EditCommand) => string | null;
  onError: (msg: string) => void;
}) {
  const s = caption.style;
  const edit = (patch: Partial<EditCommand & { op: 'editCaption' }>) => {
    const err = dispatch({ op: 'editCaption', itemId: caption.id, ...patch });
    if (err) onError(err);
  };
  const setStyle = (style: Partial<CaptionItem['style']>) => edit({ style });

  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <label className="flex items-center justify-between gap-2">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </label>
  );

  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-xs font-semibold uppercase text-muted-foreground">자막 편집</h3>
      {/* text — uncontrolled, committed on blur/Enter (key resets on selection change) */}
      <input
        key={caption.id}
        defaultValue={caption.text}
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v && v !== caption.text) edit({ text: v });
        }}
        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
        className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
        placeholder="자막 내용"
      />
      <Row label="정렬">
        <select
          value={s.alignment}
          onChange={(e) =>
            setStyle({ alignment: e.target.value as CaptionItem['style']['alignment'] })
          }
          className="rounded-md border bg-background px-1.5 py-1 text-xs"
        >
          {ALIGNMENTS.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </select>
      </Row>
      <Row label="폰트">
        <select
          value={s.fontFamily}
          onChange={(e) => setStyle({ fontFamily: e.target.value })}
          className="max-w-[9.5rem] rounded-md border bg-background px-1.5 py-1 text-xs"
          style={{ fontFamily: s.fontFamily }}
        >
          {FONT_GROUPS.map(({ label, group }) => (
            <optgroup key={group} label={label}>
              {CAPTION_FONTS.filter((f) => f.group === group).map((f) => (
                <option key={f.family} value={f.family} style={{ fontFamily: f.family }}>
                  {f.label}
                </option>
              ))}
            </optgroup>
          ))}
          {/* A caption authored with a font no longer in the catalog still shows. */}
          {!CAPTION_FONTS.some((f) => f.family === s.fontFamily) && (
            <option value={s.fontFamily}>{s.fontFamily}</option>
          )}
        </select>
      </Row>
      <Row label="크기">
        <input
          type="number"
          min={16}
          max={200}
          value={s.fontSizePx}
          onChange={(e) => setStyle({ fontSizePx: Number(e.target.value) })}
          className="w-20 rounded-md border bg-background px-1.5 py-1 text-xs"
        />
      </Row>
      <Row label="색상">
        <input
          type="color"
          value={s.primaryColor}
          onChange={(e) => setStyle({ primaryColor: e.target.value.toUpperCase() })}
          className="h-7 w-12 rounded border bg-background"
        />
      </Row>
      <Row label="외곽선">
        <input
          type="color"
          value={s.outlineColor}
          onChange={(e) => setStyle({ outlineColor: e.target.value.toUpperCase() })}
          className="h-7 w-12 rounded border bg-background"
        />
      </Row>
      <Row label="여백(px)">
        <input
          type="number"
          min={0}
          max={900}
          value={s.marginVPx}
          onChange={(e) => setStyle({ marginVPx: Number(e.target.value) })}
          className="w-20 rounded-md border bg-background px-1.5 py-1 text-xs"
        />
      </Row>
      <label className="flex items-center gap-2 text-xs text-muted-foreground">
        <input
          type="checkbox"
          checked={s.bold}
          onChange={(e) => setStyle({ bold: e.target.checked })}
        />
        볼드
      </label>
    </div>
  );
}
